import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';

interface I18nContextType {
  currentLanguage: 'es' | 'en';
  changeLanguage: (language: 'es' | 'en') => void;
  t: (key: string, options?: any) => string;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: React.ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { t, i18n } = useI18nTranslation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Wait for i18n to be ready
    if (i18n.isInitialized) {
      setIsLoading(false);
      // Force Spanish language after initialization
      if (i18n.language !== 'es') {
        i18n.changeLanguage('es');
      }
    } else {
      i18n.on('initialized', () => {
        setIsLoading(false);
        // Force Spanish language after initialization
        if (i18n.language !== 'es') {
          i18n.changeLanguage('es');
        }
      });
    }

    return () => {
      i18n.off('initialized');
    };
  }, [i18n]);

  const changeLanguage = async (language: 'es' | 'en') => {
    setIsLoading(true);
    try {
      await i18n.changeLanguage(language);
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentLanguage = (i18n.language?.startsWith('es') ? 'es' : 'en') as 'es' | 'en';

  const value: I18nContextType = {
    currentLanguage,
    changeLanguage,
    t,
    isLoading,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18nContext = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18nContext must be used within an I18nProvider');
  }
  return context;
};

export default I18nProvider;